<template>
  <section class="dw" :class="{ 'dw--menu-open': menuOpen, 'dw--mobile': mobile, 'dw--reordering': reordering }">
    <header class="dw-head" :class="{ 'dw-head--drag': draggable }" :title="draggable ? $tn('dashboardWidgets.dragHint') : null">
      <i v-if="draggable" class="fas fa-grip-vertical dw-grip" aria-hidden="true"></i>
      <i v-if="icon" class="fas dw-icon" :class="`fa-${icon}`" aria-hidden="true"></i>
      <h2 class="dw-title" :title="title">{{ title }}</h2>
      <div class="dw-actions">
        <slot v-if="!mobile" name="actions" />
        <template v-if="reordering">
          <button type="button" class="dw-btn" :disabled="!canUp" :aria-label="$tn('dashboardWidgets.moveUp')" @click="$emit('up')">
            <i class="fas fa-arrow-up" aria-hidden="true"></i>
          </button>
          <button type="button" class="dw-btn" :disabled="!canDown" :aria-label="$tn('dashboardWidgets.moveDown')" @click="$emit('down')">
            <i class="fas fa-arrow-down" aria-hidden="true"></i>
          </button>
          <button type="button" class="dw-btn dw-btn--danger" :aria-label="$tn('dashboardWidgets.remove')" @click="$emit('remove')">
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>
        </template>
        <div v-else class="dw-menu">
          <button
            type="button"
            class="dw-btn"
            :aria-label="$tn('dashboardWidgets.options')"
            :aria-expanded="menuOpen ? 'true' : 'false'"
            @click.stop="toggleMenu"
          >
            <i class="fas fa-ellipsis-h" aria-hidden="true"></i>
          </button>
          <div v-if="menuOpen" class="dw-menu__list" role="menu" @click.stop>
            <template v-for="item in allItems">
              <div v-if="item.divider" :key="item.key" class="dw-menu__divider">
                <span v-if="item.label">{{ item.label }}</span>
              </div>
              <button
                v-else
                :key="item.key"
                type="button"
                role="menuitem"
                class="dw-menu__item"
                :class="{ 'is-active': item.active, 'is-danger': item.danger }"
                @click="pick(item)"
              >
                <i class="fas fa-fw" :class="item.active ? 'fa-check' : item.icon ? `fa-${item.icon}` : ''" aria-hidden="true"></i>
                <span>{{ item.label }}</span>
              </button>
            </template>
          </div>
        </div>
      </div>
    </header>
    <!-- En el teléfono los botones propios del widget van en su fila: al lado del
         título lo dejarían en «Cal…». -->
    <div v-if="mobile && $slots.actions && !reordering" class="dw-subhead">
      <slot name="actions" />
    </div>
    <div v-show="!reordering" class="dw-body" :class="{ 'dw-body--flush': flush }">
      <slot />
    </div>
  </section>
</template>

<script>
/**
 * Marco común de los widgets del dashboard: encabezado (se arrastra desde ahí en
 * escritorio), menú «⋯» con las opciones del widget + actualizar/quitar, y en el
 * teléfono las flechas para ordenar.
 */
export default {
  name: "DashboardWidgetFrame",
  props: {
    title: { type: String, default: "" },
    icon: { type: String, default: "" },
    menuItems: { type: Array, default: () => [] },
    draggable: { type: Boolean, default: false },
    mobile: { type: Boolean, default: false },
    reordering: { type: Boolean, default: false },
    canUp: { type: Boolean, default: false },
    canDown: { type: Boolean, default: false },
    refreshable: { type: Boolean, default: true },
    /** Sin padding: el contenido (calendario, resumen) trae el suyo. */
    flush: { type: Boolean, default: false },
  },
  data() {
    return { menuOpen: false };
  },
  computed: {
    allItems() {
      const items = [...this.menuItems];
      if (items.length) items.push({ divider: true, key: "frame-divider" });
      if (this.refreshable) items.push({ key: "refresh", label: this.$tn("dashboardWidgets.refresh"), icon: "sync-alt" });
      items.push({ key: "remove", label: this.$tn("dashboardWidgets.remove"), icon: "trash-alt", danger: true });
      return items;
    },
  },
  watch: {
    menuOpen(open) {
      this.$emit("menu-open", open);
      if (open) document.addEventListener("click", this.closeMenu);
      else document.removeEventListener("click", this.closeMenu);
    },
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeMenu);
  },
  methods: {
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    },
    closeMenu() {
      this.menuOpen = false;
    },
    pick(item) {
      this.menuOpen = false;
      if (item.key === "remove") this.$emit("remove");
      else this.$emit("menu", item.key);
    },
  },
};
</script>

<style scoped>
.dw {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--dw-surface);
  border: 1px solid var(--dw-border);
  border-radius: 12px;
  box-shadow: var(--dw-shadow);
  min-width: 0;
}
.dw-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px 8px 12px;
  border-bottom: 1px solid var(--dw-border);
  min-height: 42px;
  flex-shrink: 0;
  user-select: none;
}
.dw-head--drag { cursor: grab; }
.dw-head--drag:active { cursor: grabbing; }
.dw-grip { color: var(--dw-muted); opacity: 0.45; font-size: 0.8rem; }
.dw:hover .dw-grip { opacity: 0.9; }
.dw-icon { color: var(--bs-primary); font-size: 0.9rem; }
.dw-title {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 0.92rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dw-actions { display: flex; align-items: center; gap: 4px; flex-shrink: 0; cursor: default; }
.dw-btn {
  border: 0;
  background: transparent;
  color: var(--dw-muted);
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.dw-btn:hover:not(:disabled) { background: var(--dw-surface-2); color: var(--dw-text); }
.dw-btn:disabled { opacity: 0.35; cursor: default; }
.dw-btn--danger:hover:not(:disabled) { color: var(--bs-danger); }
.dw-menu { position: relative; }
.dw-menu__list {
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  z-index: 40;
  min-width: 200px;
  max-height: 320px;
  overflow-y: auto;
  padding: 4px;
  background: var(--dw-surface);
  border: 1px solid var(--dw-border);
  border-radius: 10px;
  box-shadow: var(--bs-box-shadow);
}
.dw-menu__item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 0;
  background: transparent;
  padding: 7px 10px;
  border-radius: 6px;
  text-align: left;
  font-size: 0.85rem;
  color: var(--dw-text);
  cursor: pointer;
}
.dw-menu__item:hover { background: var(--dw-surface-2); }
.dw-menu__item.is-active { color: var(--bs-primary); font-weight: 600; }
.dw-menu__item.is-danger { color: var(--bs-danger); }
.dw-menu__item i { color: inherit; opacity: 0.8; }
.dw-menu__divider { border-top: 1px solid var(--dw-border); margin: 4px 2px; padding-top: 4px; }
.dw-menu__divider span { display: block; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--dw-muted); padding: 2px 8px; }
.dw-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 10px 12px;
}
.dw-body--flush { padding: 0; }
.dw--mobile .dw-body { flex: 0 0 auto; overflow: visible; }
.dw--mobile .dw-head { padding-left: 14px; }
/* Ordenando en el teléfono: solo los encabezados, como una lista. */
.dw--reordering .dw-head { border-bottom: 0; }
.dw-subhead {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--dw-border);
}
/* En el teléfono los widgets toman su alto natural; los de datos se acotan para no
   hacer una página interminable. */
.dw--mobile.dw--capped .dw-body { max-height: 420px; overflow-y: auto; }
.dw ::v-deep .dw-empty { margin: 0; padding: 12px 0; font-size: 0.85rem; color: var(--dw-muted); text-align: center; }
</style>
